import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from 'react'
import {toPairs, Dictionary, sortBy} from 'lodash'
import {useQuery, useMutation} from '@apollo/client'
import {v4 as uuid4} from 'uuid'

import {useAuth} from './UserContext'
import {fetcher} from '../api/rest'
import {getCartQuantityMap} from '../utils/cart'
import {fromGlobalId, intFromGlobalId} from '../utils/graphql'
import {ensureList} from '../utils/typing'
import {getAbsoluteUrl} from '../utils/url'

import {
  myCheckoutQuery,
  checkoutCreateMutation,
  checkoutLinesUpdateMutation,
  checkoutCompleteMutation,
  checkoutShippingMethodUpdateMutation,
  checkoutPaymentCreateMutation,
} from './cart-queries'

import {
  MyCheckout,
  MyCheckout_me_checkout,
} from './_gql-types/MyCheckout'
import {
  CheckoutCreate,
  CheckoutCreate_checkoutCreate_checkout,
} from './_gql-types/CheckoutCreate'
import {
  CheckoutLinesUpdate,
  CheckoutLinesUpdateVariables,
  CheckoutLinesUpdate_checkoutLinesUpdate,
} from './_gql-types/CheckoutLinesUpdate'
import {
  CheckoutComplete,
  CheckoutComplete_checkoutComplete,
} from './_gql-types/CheckoutComplete'
import {CheckoutShippingMethodUpdate} from './_gql-types/CheckoutShippingMethodUpdate'
import {CheckoutPaymentCreate} from './_gql-types/CheckoutPaymentCreate'


interface CartContextProps {
  cart: MyCheckout_me_checkout
  cartLoading: boolean
  shippingPrice?: number
  cartUpdate: (state: Dictionary<number>) => Promise<CheckoutLinesUpdate_checkoutLinesUpdate | undefined>
  cartUpdateMeta: (data: Dictionary<string>) => Promise<void>
  completeCheckout: () => Promise<CheckoutComplete_checkoutComplete | undefined>,
}


export const CartContext = createContext<Partial<CartContextProps>>({})


interface Props {
  children: React.ReactNode
}


function findCheapestShippingMethod(cart?: MyCheckout_me_checkout) {
  return sortBy(
    ensureList(cart?.availableShippingMethods),
    (m) => m.price?.amount,
  )[0]
}


export function CartProvider({children}: Props) {
  const {user, company, authToken} = useAuth()
  const {data, loading, refetch} = useQuery<MyCheckout>(myCheckoutQuery, {
    skip: user == null
  })

  const [checkoutCreate] = useMutation<CheckoutCreate>(checkoutCreateMutation)

  const mutationOptions = {
    refetchQueries: [
      {query: myCheckoutQuery},
    ]
  }
  const [checkoutLinesUpdate] = useMutation<CheckoutLinesUpdate, CheckoutLinesUpdateVariables>(
    checkoutLinesUpdateMutation,
    mutationOptions,
  )
  const [checkoutShippingMethodUpdate] = useMutation<CheckoutShippingMethodUpdate>(
    checkoutShippingMethodUpdateMutation,
  )
  const [checkoutPaymentCreate] = useMutation<CheckoutPaymentCreate>(
    checkoutPaymentCreateMutation,
  )
  const [checkoutComplete] = useMutation<CheckoutComplete>(
    checkoutCompleteMutation,
    mutationOptions,
  )

  const cart = data?.me?.checkout || undefined

  useEffect(() => {
    // Make sure that the cart has a selected shipping address based
    // on which company is selected
    if (company && cart) {
      const {id} = fromGlobalId(cart.id)

      const currentShippingAddressId = cart.shippingAddress
        ? intFromGlobalId(cart.shippingAddress.id).id
        : undefined
      const currentBillingAddressId = cart.billingAddress
        ? intFromGlobalId(cart.billingAddress.id).id
        : undefined

      const companyAddressIds = company.addresses.map((a) => a.id)

      const changes: any = {}

      if (!currentShippingAddressId || !companyAddressIds.includes(currentShippingAddressId)) {
        changes['shipping_address'] = company.default_shipping_address || company.default_billing_address
      }

      if (!currentBillingAddressId || !companyAddressIds.includes(currentBillingAddressId)) {
        changes['billing_address'] = company.default_billing_address || company.default_shipping_address
      }

      if (Object.keys(changes).length) {
        fetcher.patch(`/checkouts/${id}/`, authToken, changes).then(() => {
          refetch()
        })
      }
    }
  }, [cart, company, authToken, refetch])

  const cartUpdate = useCallback(async (stateUpdate: Dictionary<number>) => {
    if (user == null) {
      throw new Error('Cannot update cart unless a user is logged in')
    }

    // Make sure that we have the latest checkout id:
    let checkout = await (await refetch()).data?.me?.checkout

    if (checkout == null) {
      const res = await checkoutCreate()
      checkout = res.data?.checkoutCreate?.checkout as CheckoutCreate_checkoutCreate_checkout
    }

    const currentState = getCartQuantityMap(checkout)
    const stateUpdatePairs = toPairs(stateUpdate)

    const toUpdate = stateUpdatePairs.filter(([vId, qty]) => currentState[vId] !== qty)

    const res = toUpdate.length
      ? await checkoutLinesUpdate({
          variables: {
            checkoutId: checkout.id,
            lines: toUpdate.map(([variantId, quantity]) => ({variantId, quantity})),
          }
        })
      : undefined

    return res?.data?.checkoutLinesUpdate || undefined
  }, [refetch, user, checkoutLinesUpdate, checkoutCreate])

  const cartUpdateMeta = useCallback(async (data: Dictionary<string>) => {
    if (!cart) {
      return
    }
    const {id} = fromGlobalId(cart.id)
    await fetcher.post(`/checkouts/${id}/update-meta/`, authToken, data)
  }, [cart, authToken])

  const completeCheckout = useCallback(async () => {
    // Make sure that we have the latest checkout id:
    let checkout = await (await refetch()).data?.me?.checkout
    if (!checkout) {
      // TODO: Log to sentry
      return undefined
    }

    const shipping = findCheapestShippingMethod(checkout)
    if (!shipping) {
      // TODO: Log to sentry
      return undefined
    }

    const smUpdateRes = await checkoutShippingMethodUpdate({
      variables: {
        checkoutId: checkout.id,
        shippingMethodId: shipping.id,
      },
    })

    checkout = smUpdateRes.data?.checkoutShippingMethodUpdate?.checkout || checkout

    const paymentCreateRes = await checkoutPaymentCreate({
      variables: {
        checkoutId: checkout.id,
        input: {
          gateway: checkout.availablePaymentGateways?.[0]?.id,
          amount: checkout.totalPrice?.gross.amount,
          token: uuid4(),
        },
      },
    })

    checkout = paymentCreateRes?.data?.checkoutPaymentCreate?.checkout || checkout

    const res = await checkoutComplete({
      variables: {
        checkoutId: checkout.id,
        redirectUrl: getAbsoluteUrl('/verslun/pantanir/'),
      }
    })

    const ret = res?.data?.checkoutComplete || undefined

    // Connect order to company and create in NAV. Fire and forget:
    fetcher.post(`/orders/${ret?.order?.token}/postprocess/?company=${company?.id}`, authToken)

    return ret
  }, [refetch, checkoutComplete, checkoutPaymentCreate, checkoutShippingMethodUpdate, authToken, company])

  const context: Partial<CartContextProps> = {
    cart,
    cartLoading: loading,
    shippingPrice: findCheapestShippingMethod(cart)?.price?.amount,
    cartUpdate,
    cartUpdateMeta,
    completeCheckout,
  }

  return (
    <CartContext.Provider value={context}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  return useContext(CartContext) as CartContextProps
}
